import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActionsBar from '../shared/DialogActionsBar';
import { useGlobalStore } from '../../stores/globalStore';
import { readUserState, useServerUserState, writeUserState } from '../../hooks/useServerUserState';
import { useWelcomeTourStore } from './welcomeTourStore';
import { WELCOME_TOUR_PREF, type WelcomeTourRecord } from './welcomeTourModel';
import { RELEASE_NOTES, type ReleaseNote } from '../../releaseNotes/releaseNotes';
import { WHATS_NEW_SEEN_PREF, decideWhatsNew, formatNoteDate } from '../../releaseNotes/releaseNotesModel';
import ReleaseNoteItems from '../../releaseNotes/ReleaseNoteItems';

/**
 * Mounted once in MainLayout beside WelcomeTourHost. Shows a release note flagged `announce` once
 * to each user, after the welcome tour and never alongside it (releaseNotesModel.decideWhatsNew has
 * the rules). Close, "See all changes" and "Read more" all store the note's id, so it does not come
 * back. The id is kept with the user on the server and in this browser (useServerUserState): once
 * per person, not per browser, and nothing shows until the server's copy has loaded (or failed).
 * Most releases are not announced: their notes are only in Help > What's New.
 */
export default function WhatsNewNotice({ notes = RELEASE_NOTES }: { notes?: ReleaseNote[] }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const userId = useGlobalStore((s) => s.userId);
  const role = useGlobalStore((s) => s.userRole);
  const tourOpen = useWelcomeTourStore((s) => s.open);
  const tourPaused = useWelcomeTourStore((s) => s.paused);
  const [note, setNote] = useState<ReleaseNote | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const { ready: stateReady } = useServerUserState();

  useEffect(() => {
    if (note || userId <= 0 || !stateReady) return;
    // Read the tour's state fresh: WelcomeTourHost may have opened it in this same commit.
    const tour = useWelcomeTourStore.getState();
    const decision = decideWhatsNew({
      notes,
      seen: readUserState<string | null>(userId, WHATS_NEW_SEEN_PREF, null),
      pathname,
      role,
      tourRecord: readUserState<WelcomeTourRecord | null>(userId, WELCOME_TOUR_PREF, null),
      tourActive: tour.open || !!tour.paused,
    });
    if (decision.kind === 'baseline') writeUserState<string>(userId, WHATS_NEW_SEEN_PREF, decision.id);
    else if (decision.kind === 'show') setNote(decision.note);
  }, [note, notes, userId, role, pathname, tourOpen, tourPaused, stateReady]);

  const dismiss = useCallback(() => {
    if (note && userId > 0) writeUserState<string>(userId, WHATS_NEW_SEEN_PREF, note.id);
    setNote(null);
  }, [note, userId]);

  const seeAll = () => {
    dismiss();
    navigate('/help?tab=whatsnew');
  };

  if (!note) return null;
  return (
    <Dialog
      open
      onClose={dismiss}
      maxWidth="sm"
      fullWidth
      aria-labelledby="whats-new-title"
      data-testid="whats-new-notice"
      slotProps={{ transition: { onEntered: () => closeRef.current?.focus() } }}
    >
      <DialogTitle id="whats-new-title">
        What's changed
        <span style={{ display: 'block', fontSize: 'var(--font-size-base)', fontWeight: 400, color: 'var(--text-secondary)' }}>
          {formatNoteDate(note.date)}
        </span>
      </DialogTitle>
      <DialogContent>
        <ReleaseNoteItems items={note.items} onOpenArticle={dismiss} />
      </DialogContent>
      <DialogActionsBar
        onCancel={dismiss}
        cancelLabel="Close"
        onSave={seeAll}
        saveLabel="See all changes"
        enterSaves={false}
        initialFocus="cancel"
        cancelRef={closeRef}
      />
    </Dialog>
  );
}
